import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ChevronLeft, Calculator, Shapes, Hash, BookOpen,
  CheckCircle, XCircle, Star, Trophy, ArrowRight,
  RefreshCw, Volume2, Target, Clock
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Activity types
const ACTIVITIES = [
  { id: "calcul", name: "Calcul mental", icon: Calculator, color: "blue", emoji: "🧮" },
  { id: "probleme", name: "Problemes", icon: BookOpen, color: "green", emoji: "📝" },
  { id: "geometrie", name: "Geometrie", icon: Shapes, color: "orange", emoji: "📐" },
  { id: "numeration", name: "Numeration", icon: Hash, color: "purple", emoji: "🔢" },
];

function ExerciseCard({ exercise, onAnswer, showResult, userAnswer, isCorrect }) {
  return (
    <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 max-w-lg mx-auto">
      {/* Question */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <span className="text-3xl">🧮</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 leading-relaxed">
          {exercise.question}
        </h2>
      </div>

      {/* Options */}
      {exercise.options && (
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {exercise.options.map((option, i) => {
            const isSelected = userAnswer === option;
            const isCorrectOption = option === exercise.answer;
            let bgColor = "bg-slate-100 hover:bg-slate-200";
            let textColor = "text-slate-700";
            let borderColor = "border-transparent";
            
            if (showResult) {
              if (isCorrectOption) {
                bgColor = "bg-green-100";
                textColor = "text-green-700";
                borderColor = "border-green-400";
              } else if (isSelected && !isCorrectOption) {
                bgColor = "bg-red-100";
                textColor = "text-red-700";
                borderColor = "border-red-400";
              }
            } else if (isSelected) {
              bgColor = "bg-blue-100";
              textColor = "text-blue-700";
              borderColor = "border-blue-400";
            }
            
            return (
              <button
                key={i}
                onClick={() => !showResult && onAnswer(option)}
                disabled={showResult}
                className={`${bgColor} ${textColor} border-2 ${borderColor} rounded-2xl p-4 md:p-6 text-xl md:text-2xl font-bold transition-all hover:scale-105 active:scale-95 disabled:hover:scale-100`}
              >
                {option}
                {showResult && isCorrectOption && (
                  <CheckCircle className="inline ml-2 text-green-500" size={24} />
                )}
                {showResult && isSelected && !isCorrectOption && (
                  <XCircle className="inline ml-2 text-red-500" size={24} />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Result feedback */}
      {showResult && (
        <div className={`mt-6 p-4 rounded-2xl text-center ${isCorrect ? "bg-green-50" : "bg-red-50"}`}>
          {isCorrect ? (
            <div className="flex items-center justify-center gap-2 text-green-600">
              <Star className="fill-yellow-400 text-yellow-400" size={28} />
              <span className="text-xl font-bold">Bravo !</span>
              <Star className="fill-yellow-400 text-yellow-400" size={28} />
            </div>
          ) : (
            <div className="text-red-600">
              <p className="font-bold text-lg">Pas tout a fait...</p>
              <p className="text-sm mt-1">La bonne reponse etait : <strong>{exercise.answer}</strong></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MathPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userAnswer, setUserAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [startTime, setStartTime] = useState(null);

  const fetchExercise = useCallback(async (type) => {
    setLoading(true);
    setUserAnswer(null);
    setShowResult(false);
    setStartTime(Date.now());
    
    try {
      const res = await fetch(`${API_URL}/api/math/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": user?.id || "",
        },
        body: JSON.stringify({
          exercise_type: type,
          niveau: user?.niveau || "cp",
          difficulty: 1,
        }),
      });
      const data = await res.json();
      setExercise(data);
    } catch (err) {
      console.error("Failed to fetch exercise:", err);
    }
    setLoading(false);
  }, [user]);

  const handleAnswer = async (answer) => {
    setUserAnswer(answer);
    const isCorrect = answer === exercise.answer;
    setShowResult(true);
    
    // Update score
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1,
    }));

    // Save result
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      await fetch(`${API_URL}/api/math/result`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": user?.id || "",
        },
        body: JSON.stringify({
          exercise_id: exercise.id,
          correct: isCorrect,
          time_seconds: timeSpent,
          answer_given: answer,
        }),
      });
    } catch (err) {
      console.error("Failed to save result:", err);
    }
  };

  const nextExercise = () => {
    fetchExercise(selectedActivity);
  };

  const resetGame = () => {
    setScore({ correct: 0, total: 0 });
    setExercise(null);
    setSelectedActivity(null);
  };

  // Activity selection view
  if (!selectedActivity) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button
              onClick={() => navigate("/home")}
              className="p-2 hover:bg-slate-100 rounded-xl"
            >
              <ChevronLeft size={24} className="text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <span className="text-3xl">🔢</span> Mathematiques
              </h1>
              <p className="text-sm text-slate-500">Niveau {user?.niveau?.toUpperCase() || "CP"}</p>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-xl font-bold text-slate-700 mb-6">Choisis une activite</h2>
          
          <div className="grid grid-cols-2 gap-4">
            {ACTIVITIES.map((activity) => (
              <button
                key={activity.id}
                onClick={() => {
                  setSelectedActivity(activity.id);
                  fetchExercise(activity.id);
                }}
                className={`bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-all hover:scale-105 active:scale-95 border-2 border-transparent hover:border-${activity.color}-300`}
              >
                <div className={`w-16 h-16 bg-${activity.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-3`}>
                  <span className="text-4xl">{activity.emoji}</span>
                </div>
                <h3 className="font-bold text-slate-800">{activity.name}</h3>
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Exercise view
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={resetGame}
              className="p-2 hover:bg-slate-100 rounded-xl"
            >
              <ChevronLeft size={24} className="text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">
                {ACTIVITIES.find(a => a.id === selectedActivity)?.name}
              </h1>
            </div>
          </div>
          
          {/* Score */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-yellow-100 px-4 py-2 rounded-xl">
              <Trophy className="text-yellow-500" size={20} />
              <span className="font-bold text-yellow-700">{score.correct}/{score.total}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Chargement...</p>
          </div>
        ) : exercise ? (
          <>
            <ExerciseCard
              exercise={exercise}
              onAnswer={handleAnswer}
              showResult={showResult}
              userAnswer={userAnswer}
              isCorrect={userAnswer === exercise.answer}
            />

            {showResult && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={nextExercise}
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2"
                >
                  Suivant <ArrowRight size={20} />
                </button>
              </div>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}
